import { Icon, styled, useTheme } from '@mui/material';
import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { TableV1 } from '../../../components/Table/TableV1';
import { ROUTES } from '../../../constants/routes';
import { useTimesheets } from '../../../queries/hooks/useTimesheets';
import { usePendingTimesheetsTableColumns } from '../hooks/usePendingTimesheetsTableColumns';
import { FilterSection } from './FilterSection';
import { useAtom, useAtomValue } from 'jotai';
import { userState } from '../../../state/UIState';
import { showOnboardingState } from '../../../state/UITimesheetsState';
import { TimesheetStatus, UserRole } from '../../../types';
import { useProxyIssuedTimesheetsTableColumns } from '../hooks/useProxyIssuedTimesheetsTableColumns';
import OnboardingIssuedTimesheets from './OnboardingIssuedTimesheets';
import { TS_MOCK_DATA } from '../../../components/Onboarding/OnboardingMockData';
import { mapTsStatuses } from '../../../utils/tsMappers';
import { Typography } from '../../../components/Typography/Typography';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-circle.svg';

export function IssuedTimesheetsTable() {
  const [showOnboarding, setShowOnboarding] = useAtom(showOnboardingState);
  const { colors } = useTheme();
  const user = useAtomValue(userState);
  const { pathname } = useLocation();

  // This component is shared by two routes: /pending-timesheets (Submitter — own sheets) and
  // /issued-timesheets (Proxy — the ISSUED sheets of the submitters in the Proxy configuration). Drive
  // everything by the ROUTE, not by isSubmitter/firstRole, otherwise a multi-role user (who is also a
  // Submitter) wrongly sees their OWN timesheets on the Proxy's Issued Timesheets page.
  const isProxyView = pathname.includes(ROUTES.ISSUED_TIMESHEETS);
  const isOP = !isProxyView;

  const { isLoading: areIssuedTimesheetsLoading, data: timesheets } = useTimesheets({
    userId: user?.id,
    role: isProxyView ? UserRole.PROXY : UserRole.OPERATING_PARTNER,
  });

  useEffect(() => {
    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding');
    if (hasCompletedOnboarding) {
      setShowOnboarding(false);
    }
  }, [setShowOnboarding]);

  const issuedTimesheets = useMemo(() => {
    if (!timesheets) return [];
    if (isOP)
      return timesheets.filter(
        (ts) => ts.status === TimesheetStatus.ISSUED || ts.status === TimesheetStatus.REJECTED
      );
    return timesheets.filter((ts) => ts.status === TimesheetStatus.ISSUED);
  }, [timesheets, isOP]);

  const countRejectedTS = useMemo(() => {
    return timesheets?.filter((ts) => ts.status === TimesheetStatus.REJECTED).length;
  }, [timesheets]);

  const statusMappedData = useMemo(() => {
    return mapTsStatuses(issuedTimesheets, UserRole.OPERATING_PARTNER);
  }, [issuedTimesheets]);

  // Call BOTH column hooks unconditionally (Rules of Hooks): the two routes reuse the same component
  // instance, so conditionally choosing WHICH hook to call would change hook order on route switch.
  const pendingColumns = usePendingTimesheetsTableColumns();
  const proxyColumns = useProxyIssuedTimesheetsTableColumns();
  const columns = isProxyView ? proxyColumns : pendingColumns;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const table = useReactTable({
    data: showOnboarding ? TS_MOCK_DATA : statusMappedData ?? [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (issuedItem) => String(issuedItem.id),
  });

  if (areIssuedTimesheetsLoading || !timesheets) return <TableSkeletonLoader />;

  return (
    <div>
      {isOP && showOnboarding && (
        <OnboardingIssuedTimesheets setShowOnboarding={setShowOnboarding} />
      )}
      {isOP && !!countRejectedTS && (
        <RejectedBanner>
          <Icon sx={{ svg: { path: { fill: colors.iconStatus.error } } }}>
            <WarningIcon />
          </Icon>
          <Typography variant='h5' color={colors.textStatus.error}>
            {`${countRejectedTS} ${countRejectedTS > 1 ? 'Timesheets' : 'Timesheet'} Sent Back`}
          </Typography>
        </RejectedBanner>
      )}
      <FilterSection table={table} isProxyView={isProxyView} />
      <TableV1
        className='timesheet-table'
        table={table}
        height='auto'
        wrapperStyle={{
          maxHeight: showOnboarding ? 'calc(100vh - 525px)' : 'calc(100vh - 288px)',
          border: `1px solid ${colors.border.default}`,
          overflowY: 'scroll',
        }}
      />
    </div>
  );
}

const RejectedBanner = styled('div')`
  background-color: ${({ theme }) => theme.colors.surfaceBackground.highlighted};
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

